import { useState, useEffect } from "react";
import { MAIN_URL } from "../config";
import axios from "axios";

interface File {
  code: string;
  name: string;
  uid: string;
  wording: string;
  status: string;
  stage_file_title: string;
  state_file_title: string;
  state_file_code: string;
  active: boolean;
}

interface FilesData {
  Files: File[];
  total_pages: number;
}

interface UseFilesDataResponse {
  data: FilesData | null;
  loading: boolean;
  error: string | null;
}

const useFile = (
  page: number,
  lawType?: string,
  userId?: string
): UseFilesDataResponse => {
  const [data, setData] = useState<FilesData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `https://oparl-textprovider-production.up.railway.app/files`,
          //`http://localhost:1080/files`,
          {
            params: {
              page: page,
              userId: userId,
              ...(lawType && { lawType }),
            },
          }
        );
        setData(response.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          setError(`Error: ${err.response.statusText}`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, lawType, userId]);

  return { data, loading, error };
};

// New hook for followed files
const useFollowedFiles = (
  userId?: string,
  limit: number = 3
): UseFilesDataResponse => {
  const [data, setData] = useState<FilesData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `https://oparl-textprovider-production.up.railway.app/files`,
          {
            //const response = await axios.get(`http://localhost:1080/files`, {
            params: {
              follow: true,
              userId: userId,
              limit: limit,
            },
          }
        );
        setData(response.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          setError(`Error: ${err.response.statusText}`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, limit]);

  return { data, loading, error };
};

// New hook for not followed files (suggestions)
const useNotFollowedFiles = (
  userId?: string,
  limit?: number
): UseFilesDataResponse => {
  const [data, setData] = useState<FilesData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `https://oparl-textprovider-production.up.railway.app/files`,
          {
            params: {
              notFollowed: true,
              userId: userId,
              ...(limit && { limit }),
            },
          }
        );
        setData(response.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          setError(`Error: ${err.response.statusText}`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, limit]);

  return { data, loading, error };
};

export default useFile;
export { useFollowedFiles, useNotFollowedFiles };
